import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  #cartSyncRequest = 0;

  /** @type {number} */
  get currentCartCount() {
    const count = Number.parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
    return Number.isFinite(count) ? count : 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '99+';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const data = event.detail?.data ?? {};
    const hasEventCount = data.itemCount !== undefined && data.itemCount !== null;
    const itemCount = Number(data.itemCount);
    const comingFromProductForm = data.source === 'product-form-component';

    // Give immediate feedback from the cart event when it includes a valid count.
    if (hasEventCount && Number.isFinite(itemCount) && itemCount >= 0) {
      this.renderCartBubble(itemCount, comingFromProductForm, itemCount > 0);
    }

    // Reconcile to Shopify's authoritative cart quantity so the badge cannot get out of sync.
    const syncToken = ++this.#cartSyncRequest;
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      if (syncToken !== this.#cartSyncRequest || !this.isConnected) return;

      this.renderCartBubble(Number(cart.item_count) || 0, false, false);
    } catch (error) {
      // The event-derived count remains visible if cart synchronization is temporarily unavailable.
      console.warn('Unable to sync the cart count.', error);
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    const parsedCount = Number(itemCount);
    const safeCount = Number.isFinite(parsedCount) ? Math.max(0, Math.floor(parsedCount)) : 0;
    const totalCount = comingFromProductForm ? this.currentCartCount + safeCount : safeCount;
    const hasCartItems = totalCount > 0;

    this.currentCartCount = totalCount;
    this.refs.cartBubbleCount.classList.toggle('hidden', !hasCartItems);
    this.refs.cartBubble.classList.toggle('visually-hidden', !hasCartItems);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', hasCartItems && animate);
    this.classList.toggle('header-actions__cart-icon--has-cart', hasCartItems);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(totalCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || !hasCartItems) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
